// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    canApplyPromotionsToCart,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@turnerBlueprint/Overrides/Components/CartTotalDisplay";
import StyledBox, { StyledBoxStyles } from "@turnerBlueprint/XDL/Components/Common/Box";
import { Divider } from "@turnerBlueprint/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotions = getCurrentPromotionsDataView(state).value;
    return {
        cart: cartState.value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
        isLoading: !cartState.value || cartState.isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        canApplyPromosToCart: canApplyPromotionsToCart(cartState.value),
        hidePromoCode: getBillToState(state, cartState.value ? cartState.value.billToId : undefined).value?.properties[
            "hidePromoCode"
        ],
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutOrderSummaryStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    cartTotalGridItem?: GridItemProps;
    cartTotal?: CartTotalDisplayStyles;
    // XNG-Changes
    styledBox?: StyledBoxStyles;
}

export const orderSummaryStyles: OnePageCheckoutOrderSummaryStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    cartTotalGridItem: { width: 12 },
    // cartTotal: {
    //     container: {
    //         gap: 10,
    //         css: css`
    //             padding: 0;
    //             background-color: transparent;
    //         `,
    //     },
    // },
};

const styles = orderSummaryStyles;

const OnePageCheckoutOrderSummary = ({
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    cartUrl,
    // XNG-Changes
    hidePromoCode,
    canApplyPromosToCart,
    id,
}: Props) => {
    // if (isLoading) {
    //     return (
    //         <StyledWrapper {...styles.centeringWrapper}>
    //             <LoadingSpinner data-test-selector="onePageCheckout_orderSummary" />
    //         </StyledWrapper>
    //     );
    // }

    // XNG-Changes: Structure
    return (
        <StyledBox extendedStyles={styles.styledBox}>
            <CartTotalDisplay
                showTaxAndShipping={true}
                isLoading={isLoading}
                extendedStyles={styles.cartTotal}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            />
            {canApplyPromosToCart && hidePromoCode !== "true" && (
                <>
                    <Divider margin="12px 0 " />
                    {createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPromotionCode", {
                        fields: {},
                    })}
                </>
            )}
            <Divider margin="12px 0 " />
            <XngZone contentId={id} zoneName="actions" />
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutOrderSummary),
    definition: {
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
        displayName: "Order Summary",
    },
};

export default widgetModule;
